import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta, eur, initials } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Users, Compass, Gem, ChevronDown, CalendarPlus } from "lucide-react";

export default function CoachHub() {
  const t = useT();
  usePageMeta(t("bkc.hub_title"));
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState(null);

  useEffect(() => {
    api.get("/auth/my-coaches")
      .then(({ data }) => setCoaches(Array.isArray(data) ? data : []))
      .catch(() => setCoaches([]));
  }, []);

  if (coaches === null) return <Spinner label={t("common.loading")} />;

  return (
    <div className="space-y-6" data-testid="client-coach-hub">
      <PageHeader
        eyebrow={t("nav.group.book")}
        title={t("bkc.hub_title")}
        subtitle={t("bkc.hub_sub")}
        action={
          <Button variant="outline" className="gap-1.5 rounded-full" asChild data-testid="hub-browse-all">
            <Link to="/coaches"><Compass className="h-4 w-4" /> {t("bkc.browse_all")}</Link>
          </Button>
        }
      />
      {coaches.length === 0 ? (
        <EmptyState
          icon={Users}
          title={t("bkc.no_coaches")}
          description={t("bkc.no_coaches_sub")}
          action={<Button className="rounded-full" asChild data-testid="hub-empty-browse"><Link to="/coaches">{t("bkc.browse_all")}</Link></Button>}
        />
      ) : (
        <div className="space-y-4">
          {coaches.map((c) => <CoachCard key={c.id} c={c} t={t} navigate={navigate} />)}
        </div>
      )}
    </div>
  );
}

function CoachCard({ c, t, navigate }) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && products === null) {
      try {
        const { data } = await api.get(`/coach/products?coach_id=${encodeURIComponent(c.id)}`);
        setProducts(Array.isArray(data) ? data : []);
      } catch { setProducts([]); }
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`coach-card-${c.id}`}>
      <div className="flex items-start gap-4">
        <Avatar className="h-14 w-14 shrink-0">
          {c.photo ? <AvatarImage src={fileUrl(c.photo)} alt={c.name} /> : null}
          <AvatarFallback>{initials(c.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-display text-lg text-foreground">{c.name}</h3>
            {c.is_inner_circle ? (
              <Badge className="gap-1 rounded-full"><Gem className="h-3 w-3" /> Inner Circle</Badge>
            ) : null}
          </div>
          {c.profession ? <p className="text-sm text-muted-foreground">{c.profession}</p> : null}
          {c.bio ? <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{c.bio}</p> : null}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="rounded-full" asChild data-testid={`coach-profile-${c.id}`}>
              <Link to={`/coaches/${c.id}`}>{t("bkc.view_profile")}</Link>
            </Button>
            <Button size="sm" className="gap-1.5 rounded-full" onClick={toggle} data-testid={`coach-offerings-${c.id}`}>
              <CalendarPlus className="h-4 w-4" /> {t("bkc.offerings")}
              <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </Button>
          </div>
        </div>
      </div>

      {open && (
        <div className="mt-4 border-t border-border pt-4" data-testid={`coach-products-${c.id}`}>
          {products === null ? (
            <Spinner className="min-h-[80px]" />
          ) : products.length === 0 ? (
            <p className="text-sm italic text-muted-foreground">{t("bkc.no_products")}</p>
          ) : (
            <div className="space-y-2">
              {products.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/app/book/${p.id}`)}
                  data-testid={`product-${p.id}`}
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-border bg-secondary/40 p-3 text-left transition-colors hover:bg-accent/40"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{p.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.duration_min} {t("bkc.min")}{p.is_group ? ` · ${t("bkc.group")}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-sm font-medium text-primary">
                      {p.payment_modes?.includes("credits") ? t("bkc.pay_credits", { n: p.price_credits }) : eur(p.price_cents)}
                    </span>
                    <span className="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">{t("bkc.book_cta")}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
